<template>
  <v-card flat>
    <!-- current vehicle -->
    <SelectedVehicleCard />
    <!-- button band -->
    <v-card-text>
      <v-btn
        block
        color="primary"
        elevation="3"
        class="text-none rounded-lg mb-3"
        @click="handelViewBooking"
      >
        Book Vehicle
      </v-btn>
      <v-btn
        block
        :color="pwtDarkBlue"
        outlined
        tile
        class="text-none rounded-lg mb-3"
        @click="handelViewAllVehicles"
      >
        View All Vehicle Options
      </v-btn>
      <v-btn
        block
        color="primary"
        text
        class="text-none rounded-lg"
        @click="handelShowEVs"
      >
        {{
          showingConnectedVehicles
            ? "Hide connected vehicles"
            : "Show connected vehicles"
        }}
      </v-btn>
    </v-card-text>
    <!-- range adjustment -->
    <v-card-title class="d-flex justify-space-between">
      <span>Range adjustment</span>
      <span>
        <StyledToolTip v-if="tooltipContent" :data="tooltipContent" />
      </span>
    </v-card-title>
    <v-card-subtitle>
      Battery health and vehicle performance change over time. Make adjustments
      to this vehicles' effective range to create more accurate, personalised
      trip plans
    </v-card-subtitle>
    <v-card-text class="d-flex flex-column">
      <v-row no-gutters justify="space-between">
        <span>Range adjustment</span>
        <span class="align-self-end mr-4">
          {{ SOHVal }}%{{ getMaxRange() }}
        </span>
      </v-row>
      <v-slider
        v-model="SOHVal"
        hide-details
        track-color="grey lighten-2"
        @end="updateSOH"
        class="ml-n2"
      />
    </v-card-text>
    <!-- driver profile -->
    <DriverProfile />
  </v-card>
</template>

<script lang="ts">
import Vue from "vue";
import { mapState, mapGetters } from "vuex";
import DriverProfile from "./DriverProfile.vue";
import SelectedVehicleCard from "./SelectedVehicleCard.vue";
import {
  ActionTypes,
  GettersTypes,
  MainDialogContent,
  ManagedContentNames,
  MutationTypes,
  SecondaryDialogContent,
  type State,
} from "@/logic/store/store_types";
import StyledToolTip from "../../ui-elements/StyledToolTip.vue";
import Vehicle from "@/logic/classes/vehicle";
import { powerTripDarkBlue } from "@/logic/data/const";

/** `Vue component:` renders a condensed list of selectable vehicles and
 * the driver profile to be displayed in the `NavPanel` and
 * `MobileDialog` components*/
export default Vue.extend({
  name: "VehicleContent",
  components: {
    DriverProfile,
    SelectedVehicleCard,
    StyledToolTip,
  },
  computed: {
    ...mapGetters({
      vehicle: GettersTypes.selectedVehicleData,
    }),
    ...mapState({
      showingConnectedVehicles: (state: unknown) =>
        (state as State).displayConnectedVehicles,
      tooltipContent: (state: unknown) =>
        (state as State).managedContent.find(
          (content) => content.Name === ManagedContentNames.SoHToolTip
        ),
    }),
  },
  methods: {
    handelViewAllVehicles() {
      this.$store.commit(
        MutationTypes.setMainDialogContent,
        MainDialogContent.VEHICLE_GALLERY
      );
    },
    handelConnectorsAndCables() {
      this.$store.commit(
        MutationTypes.setSecondaryDialogContent,
        SecondaryDialogContent.CONNECTORS_AND_CABLE_SETTINGS
      );
    },
    handelRefresh() {
      this.$store.dispatch(ActionTypes.updateVehiclesData);
    },
    handelShowEVs() {
      this.$store.commit(
        MutationTypes.setDisplayConnectedVehicles,
        !this.showingConnectedVehicles
      );
    },
    getMaxRange(): string {
      const range = (this.vehicle as Vehicle | undefined)?.calcMaxRange(
        (this.$store.state as State).extraWeight
      );
      if (range) return ` - ${Math.floor(range / 1000)}km max range`;
      return " - unknown max range";
    },
    updateSOH() {
      if (!this.vehicle) return;
      (this.vehicle as Vehicle).updateSOH(this.SOHVal / 100);
      this.$store.commit(MutationTypes.updateIndividualVehicle, this.vehicle);
    },
    handelViewBooking() {
      if (this.vehicle) {
        this.$store.commit(
          MutationTypes.setViewedFleetVehicle,
          (this.vehicle as Vehicle).localId
        );
        this.$store.commit(
          MutationTypes.setMainDialogContent,
          MainDialogContent.VEHICLE_BOOKING
        );
      }
    },
    setInitialSOH() {
      if (this.vehicle && !this.initialValueHasBeenSet) {
        this.SOHVal = (this.vehicle as Vehicle).stateOfHealth
          ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (this.vehicle as Vehicle).stateOfHealth! * 100
          : 50;
        this.initialValueHasBeenSet = true;
      }
    },
  },
  data() {
    return {
      SOHVal: 50,
      pwtDarkBlue: powerTripDarkBlue,
      initialValueHasBeenSet: false,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.setInitialSOH();
    });
  },
  watch: {
    vehicle() {
      this.setInitialSOH();
    },
  },
});
</script>

<style scoped>
* >>> .v-slider--horizontal .v-slider__track-container {
  height: 6px; /* override default slider thickness */
}

* >>> .v-slider__track-fill {
  border-radius: 2px; /* override default slider border-radius */
}

* >>> .v-slider__track-background {
  border-radius: 2px; /* override default slider border-radius */
}
</style>
